import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { IVEClassification } from "@/types/index"
import { supabase } from "@/lib/supabase/client"
import { isValidCNPJ as isValidCNPJShared, formatCNPJ as formatCNPJShared } from "@ive/cnpj"
import { chaveDaCidade } from "@/lib/chaveDaCidade"
import { emAnos, emDias, emMeses } from "@/lib/tempoPorExtenso"

// CNPJ (numérico legado + alfanumérico RFB) vive na fonte única @ive/cnpj,
// compartilhada com as edge functions. Re-exportado aqui p/ os consumidores
// que já importam de @/lib/utils.
export { unmaskCNPJ } from "@ive/cnpj"

/**
 * Utilitarios do CreditOS v2
 * Convencoes: interface em portugues, codigo em ingles
 */

// ─── Tailwind / CSS ─────────────────────────────────────────────────────────────

/** Combina classes Tailwind com resolucao de conflitos */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ─── Formatacao de documentos ───────────────────────────────────────────────────

/**
 * Formata CPF: 000.000.000-00
 * Remove nao-digitos antes de formatar.
 */
export function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
}


/**
 * Formata CNPJ: 00.000.000/0000-00 (numérico legado OU alfanumérico RFB).
 * Delegado à fonte única @ive/cnpj; saída idêntica à anterior p/ numérico.
 */
export function formatCNPJ(value: string): string {
  return formatCNPJShared(value)
}

/**
 * Os nomes que se repetem numa lista (unidades, por exemplo). Quem lê a tela
 * não consegue diferenciar duas entradas com o mesmo nome só pelo texto; a
 * tela usa isto para decidir quando mostrar o CNPJ ao lado do nome
 * (wdwxndzhkm, na aba Cobranças e na escolha de unidade).
 */
export function nomesDuplicados(nomes: readonly string[]): Set<string> {
  const contagem = new Map<string, number>()
  for (const nome of nomes) contagem.set(nome, (contagem.get(nome) ?? 0) + 1)
  return new Set([...contagem].filter(([, quantos]) => quantos > 1).map(([nome]) => nome))
}

/**
 * Formata numero de telefone/celular: (00) 00000-0000 ou (00) 0000-0000
 */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11)
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d{1,4})$/, "$1-$2")
  }
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d{1,4})$/, "$1-$2")
}

/**
 * Normaliza telefone celular para os ÚLTIMOS 11 dígitos (DDD + 9 + 8).
 * Usada para dedup de leads (task #155 — celular agora identifica lead existente
 * junto com CPF/CNPJ). Espelha a função `normalizePhone` em
 * supabase/functions/_shared/lead-intake.ts.
 *
 * O DB tem formatos variados pelos múltiplos entry points históricos:
 *   - "5511987654321"  ← com prefixo país (LP/webhook)
 *   - "11987654321"    ← sem prefixo (cadastro manual antigo)
 *   - "(11) 98765-4321" ← formatado (cadastro rápido cru)
 *
 * Pega os últimos 11 dígitos para casar todos os formatos. Retorna null
 * quando o input não tem dígitos suficientes (<10 = inválido).
 */
export function normalizePhone(raw: string | null | undefined): string | null {
  if (!raw) return null
  const digits = String(raw).replace(/\D/g, "")
  if (digits.length < 10) return null
  return digits.length > 11 ? digits.slice(-11) : digits
}

/**
 * Normaliza string para busca insensível a acentos/maiúsculas (task #157).
 *
 * Usa NFD (Normalization Form Decomposition) para separar caracteres-base
 * de marcas combinantes (acentos), depois remove as marcas combinantes
 * Unicode (U+0300–U+036F). Resultado: "João Sílva" → "joao silva".
 *
 * Retorna sempre string lowercase trimmed (vazia se input vazio).
 */
export function normalizeSearch(raw: string | null | undefined): string {
  if (!raw) return ""
  return String(raw)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
}

// ─── Formatacao monetaria ────────────────────────────────────────────────────────

/**
 * Formata valor em Real brasileiro: R$ 1.234,56
 *
 * Singleton do Intl.NumberFormat — instanciar é caro (~20-50ms em celular
 * antigo, parsing de locale + setup interno). Como a config nunca muda,
 * cachear como módulo-level constant evita overhead em toda chamada
 * (FB-2026-0505-C1CE — LP travando no Step3Valor por chamadas repetidas).
 */
const BRL_FORMATTER = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
})

export function formatCurrency(value: number): string {
  return BRL_FORMATTER.format(value)
}

/**
 * Formata valor compacto para cards do kanban: R$ 15k, R$ 1,2M
 */
export function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1).replace(".", ",")}M`
  }
  if (value >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(0)}k`
  }
  return formatCurrency(value)
}

// ─── Formatacao de datas ─────────────────────────────────────────────────────────

/**
 * Formata data no padrao pt-BR: dd/MM/yyyy
 */
export function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat("pt-BR").format(new Date(dateStr))
}

/**
 * Formata data e hora: dd/MM/yyyy HH:mm
 */
export function formatDateTime(dateStr: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateStr))
}

/**
 * Retorna tempo relativo em português: "há 2 dias", "há 3h", "agora".
 * A unidade concorda com o número ("há 1 mês", não "há 1 meses").
 */
export function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMinutes < 1) return "agora"
  if (diffMinutes < 60) return `há ${diffMinutes} min`
  if (diffHours < 24) return `há ${diffHours}h`
  if (diffDays < 30) return `há ${emDias(diffDays)}`
  if (diffDays < 365) return `há ${emMeses(Math.floor(diffDays / 30))}`
  return `há ${emAnos(Math.floor(diffDays / 365))}`
}

// ─── IVE ─────────────────────────────────────────────────────────────────────────

/**
 * Retorna a cor CSS do semaforo IVE baseada na classificacao.
 * Mapeia para as CSS vars do design system.
 */
export function getIVEColor(classificacao: IVEClassification | null | undefined): string {
  switch (classificacao) {
    case "BAIXO_RISCO":
      return "var(--ive-high)"
    case "MEDIO_RISCO":
      return "var(--ive-medium)"
    case "ALTO_RISCO":
      return "var(--ive-low)"
    case "IMPEDIDO":
      return "var(--ive-blocked)"
    default:
      return "var(--text-tertiary)"
  }
}

/**
 * Retorna a classe CSS de badge IVE para Tailwind.
 */
export function getIVEBadgeClass(classificacao: IVEClassification | null | undefined): string {
  switch (classificacao) {
    case "BAIXO_RISCO":
      return "badge-ive-high"
    case "MEDIO_RISCO":
      return "badge-ive-medium"
    case "ALTO_RISCO":
      return "badge-ive-low"
    case "IMPEDIDO":
      return "badge-ive-blocked"
    default:
      return "badge-ive-blocked"
  }
}

// ─── Strings e texto ────────────────────────────────────────────────────────────

/**
 * Retorna as iniciais do nome (ate 2 letras) em maiusculo.
 */
export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
}

/**
 * Normaliza string para comparacao: remove acentos e converte para minusculo.
 */
export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

/**
 * A chave de comparação de cidade: `chaveDaCidade` (`lib/chaveDaCidade.ts`).
 *
 * Existe com este nome porque o mapa, os clientes do SOMA, o cadastro manual e
 * a sugestão de unidade do lead já a chamavam assim. Até 06/10/2026 era uma
 * régua própria, que cortava o estado antes de tirar o espaço do fim
 * ("São José dos Campos SP " ficava com o "sp") e cortava qualquer final de
 * duas letras ("Santa Fe" virava "santa"). Agora é a mesma chave do Dashboard,
 * do filtro de cidade e da triagem do servidor (tarefa wdwxndz2y9).
 */
export function normalizeCityName(str: string): string {
  return chaveDaCidade(str)
}

/**
 * "Cidade/UF" para exibir na tela, sem repetir a UF.
 *
 * 🚨 `lead.cidade` às vezes JÁ vem com a UF colada ("Atibaia/SP"), porque a
 * origem varia: o birô manda só o nome, o wizard de ESC grava com "/UF" e a
 * captação copia o que a pessoa escreveu. Concatenar sem olhar produzia
 * "Atibaia/SP/SP" no cabeçalho do lead.
 *
 * Diferente de `normalizeCityName`, aqui o nome é preservado como está —
 * aquela serve para COMPARAR (tira acento e caixa), esta para MOSTRAR.
 */
export function cidadeComUf(cidade?: string | null, uf?: string | null): string {
  const nome = (cidade ?? "").trim()
  if (!nome) return uf?.trim() ? `?/${uf.trim()}` : "?"

  // Sufixo de UF já presente, em qualquer das variantes que aparecem em
  // produção ("/SP", "-MG", " SP") — devolve como está.
  if (/[\s/\-]+[a-zA-Z]{2}$/.test(nome)) return nome

  const sigla = (uf ?? "").trim()
  return sigla ? `${nome}/${sigla}` : nome
}

/**
 * Verifica se uma string corresponde a um termo de busca (normalizado).
 */
export function matchesSearch(text: string, search: string): boolean {
  if (!search.trim()) return true
  return normalizeString(text).includes(normalizeString(search))
}

// ─── Validacao ─────────────────────────────────────────────────────────────────

/**
 * Valida CPF pelo algoritmo de digitos verificadores.
 */
export function isValidCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, "")
  if (digits.length !== 11) return false
  if (/^(\d)\1{10}$/.test(digits)) return false

  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i)
  }
  let check = (sum * 10) % 11
  if (check === 10 || check === 11) check = 0
  if (check !== parseInt(digits[9])) return false

  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i)
  }
  check = (sum * 10) % 11
  if (check === 10 || check === 11) check = 0
  return check === parseInt(digits[10])
}

/**
 * Valida CNPJ (numérico legado OU alfanumérico RFB) pelo dígito verificador.
 * Delegado à fonte única @ive/cnpj (retrocompatível com o legado numérico).
 */
export function isValidCNPJ(cnpj: string): boolean {
  return isValidCNPJShared(cnpj)
}

// ─── Avatar helpers ───────────────────────────────────────────────────────────

/**
 * Converte o path salvo em credit_v2_profiles.avatar_url
 * (ex: "user-id/avatar-xxx.png") para a URL pública do bucket user-avatars.
 * Retorna null se path vazio.
 * Se o valor já for URL completa (http/https), retorna como está.
 */
export function getAvatarPublicUrl(path: string | null | undefined): string | null {
  if (!path) return null
  if (/^https?:\/\//.test(path)) return path
  const { data } = supabase.storage.from("user-avatars").getPublicUrl(path)
  return data.publicUrl
}

/**
 * Retorna uma cor CSS determinística baseada no hash do nome.
 * Usada como cor de fundo para avatares com iniciais.
 */
export function getAvatarColor(name: string): string {
  const colors = [
    "#3b82f6", "#8b5cf6", "#ec4899", "#10b981",
    "#f59e0b", "#06b6d4", "#f97316", "#14b8a6",
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

// ─── Misc ─────────────────────────────────────────────────────────────────────

/**
 * Gera um slug a partir de uma string.
 * Ex: "ESC Atibaia" -> "esc-atibaia"
 */
export function slugify(text: string): string {
  return normalizeString(text)
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim()
}

/**
 * Extrai apenas os digitos de uma string.
 */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, "")
}

/**
 * Delay utilitario para uso em animacoes e debounce manual.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
